import webfft from 'webfft';
import {
    chooseWaveformGain,
    createFlatlineDetector,
    detectPitchHz,
    fitSinePhase,
    frequencyToNote,
    getYFromFrequency as getYFromFrequencyPure,
    pickLabelFontSize,
} from './utils.js';
import { BUILD_COMMIT, BUILD_DATE } from './build-info.js';

let canvas;
let ctx;

let labelsCanvas;
let labelsCtx;

let cursorLineCanvas;
let cursorLineCtx;

let timeScaleCanvas;
let timeScaleCtx;

let waveformCanvas;
let waveformCtx;
let waveformGainLevel = 0.01; // Current AGC level, starts small
let previousWaveform = null; // Previous displayed waveform for correlation trigger
let previousGainLevel = 0; // Gain applied to previousWaveform when drawn (for overlap-based scaling)
// Time zoom: number of audio samples per pixel that are shown in the waveform
// window. 1 = default (one sample per pixel). <1 zooms in (less time shown);
// >1 zooms out (more time shown).
let waveformTimeZoom = 1.0;
const MIN_WAVEFORM_TIME_ZOOM = 0.1;
const MAX_WAVEFORM_TIME_ZOOM = 10.0;
const MIN_WAVEFORM_SAMPLES = 32;
const MAX_WAVEFORM_SAMPLES = 65536;
// Weight of the pitch-locked anchor bonus relative to the cross-correlation
// score in drawWaveform. 0 = pure correlation, higher = anchor dominates.
// User-controlled via the "Pitch anchor" slider.
let waveformAnchorWeight = 1.5;

// Pitch detection for the cyan fundamental overlay on the spectrogram.
// Updated once per draw() from a fixed analysis window out of the
// continuous audio buffer; `null` means no confident pitch.
let currentFundamentalHz = null;
let lastDetectedNoteName = null;
const PITCH_ANALYSIS_SAMPLES = 2048;
// Note-onset label candidate sizes (largest first) and the horizontal padding
// added around the text in its backdrop box.
const NOTE_LABEL_SIZES = [13, 11, 9, 7];
const NOTE_LABEL_PAD_X = 2;
// Note onset we've detected but not yet drawn. We hold the label back until
// either the note has lasted long enough to fit a full-size label, or a new
// note arrives (commit with the largest size that fits in the elapsed width).
// Short notes end up with small labels, long notes with big ones. Fields:
//   noteName      — nearest equal-tempered note name, e.g. "A4"
//   anchorX       — current x of the onset column; decrements with scroll
//   lineY         — y of the pitch line at onset (for vertical placement)
//   elapsedWidth  — pixels scrolled since onset; used to pick the size
let pendingNoteLabel = null;
let phosphorDecayMs = 150; // Phosphor decay time in ms (0 = off)
let lastWaveformDrawTime = 0; // Timestamp of last waveform draw

let audioContext;
let source;
let mediaStream = null; // getUserMedia MediaStream — kept so we can stop tracks and release the mic indicator
let isPaused = false;
let isVisibilityHidden = false; // True while the tab is backgrounded (Page Visibility API)
let lastDrawBodyTime = 0; // Timestamp of the last time draw() did real work (used for FPS cap)
let animationFrameId; // To store the requestAnimationFrame ID
let mouseCanvasX = -1; // Stores the mouse X position relative to the canvas, -1 when not on canvas
let mouseCanvasY = -1; // Stores the mouse Y position relative to the canvas, -1 when not on canvas

// FPS tracking variables
let fpsCounter = 0;
let fpsLastTime = 0;
let fpsDisplay;

// Performance optimization variables
let frameSkipCounter = 0;
let frameSkipThreshold = 1; // Process every Nth frame for large FFTs
let scrollingSpeed = 1.0; // Scrolling speed multiplier; 1.0 = SCROLL_REFERENCE_FPS pixels per second, independent of paint rate
let scrollAccumulator = 0; // Fractional pixels carried between frames so live scroll is time-based
// Pixels we have shifted the canvas by since the last right-edge paint. At low
// FPS or large FFTs the canvas shifts several pixels per body but only the FFT
// response paints new column data, so the unpainted pixels between columns
// would otherwise show the previous content that we just scrolled leftward
// (visible as stripes / black gaps). Accumulating the debt lets the FFT
// response paint an interpolated block that covers the whole gap.
let pendingShiftAmount = 0;
let previousFrameData = null; // Previous FFT column, used as the left endpoint when interpolating the gap

// Web Worker for FFT computation
let fftWorker;
let fftRequestId = 0;
let pendingFftRequest = null;

// Audio buffer for reprocessing
let audioBuffer = [];
let audioBufferSize = 2000; // Store enough frames to fill canvas width
let audioBufferIndex = 0;
let isReprocessing = false;

// Continuous audio buffer for temporal continuity
let continuousAudioBuffer = null;
let continuousBufferSize = 0;
let continuousBufferIndex = 0;
let continuousBufferFilled = false; // Track if buffer has been filled at least once
let latestTimeDomainData = null;
let flatlineDetector = null;
let autoRestartInProgress = false;
let lastAutoRestartAt = 0;
const AUTO_RESTART_COOLDOWN_MS = 5000;
let lastFftInputData = null;
let lastProcessedTimestamp = 0;

let minFrequency = 20; // Default to 20 Hz
let maxFrequency = 11025; // Default to 11025 Hz

// Zoom selection variables
let isSelecting = false;
let selectionStart = null;
let selectionEnd = null;
let selectionOverlay;

// Logarithmic scale conversion functions
const MIN_LOG_FREQ = Math.log(20); // Log of 20 Hz
const MAX_LOG_FREQ = Math.log(24000); // Log of 24000 Hz
const LOG_RANGE = MAX_LOG_FREQ - MIN_LOG_FREQ;
const BASE_ANIMATION_FPS = 60; // Initial FPS assumption for time calculations
const TIME_PER_PIXEL_SMOOTHING = 0.1; // EMA weight for time-per-pixel updates
const TIME_SCALE_UPDATE_INTERVAL_MS = 200; // Min interval between time scale redraws
const MIN_TIME_PER_PIXEL = 1 / (BASE_ANIMATION_FPS * 10);
// Cap the spectrogram paint rate. rAF callbacks from 120 Hz displays arrive
// much faster than the spectrogram needs — temporal detail comes from the FFT
// hop size, not the paint rate. The cap is user-controlled via the
// power-saver / high-performance slider (20–120 FPS).
//
// DRAW_INTERVAL_CUSHION_MS absorbs floating-point noise in the rAF cadence:
// on a 60 Hz display rAF fires every ~16.67 ms, and the elapsed-time comparison
// at the target boundary (e.g. 33.33 < 33.33) can flip either way, dropping us
// to every third frame (20 FPS) instead of every second (30 FPS). A 1 ms
// cushion keeps the comparison stable without measurably changing the rate.
let targetDrawFps = 30;
let targetDrawIntervalMs = 1000 / targetDrawFps;
const DRAW_INTERVAL_CUSHION_MS = 1;
// Reference rate used by the live scroll accumulator. scrollingSpeed = 1.0
// corresponds to SCROLL_REFERENCE_FPS pixels per second, matching the app's
// historical 60-FPS-uncapped behavior so existing saved slider values keep
// feeling the same regardless of the user's FPS cap.
const SCROLL_REFERENCE_FPS = 60;

let smoothedTimePerPixel = 1 / BASE_ANIMATION_FPS;
let lastFrameTimestamp = null;
let lastDrawnTimePerPixel = smoothedTimePerPixel;
let lastTimeScaleUpdate = 0;
let lastTotalTimeSpan = 0;

// Audio buffer based timing tracking
let audioBuffersProcessed = 0;
let audioTimingStartTime = null;

// Helper function to convert webfft interleaved output to fft-js format
function convertWebfftOutput(interleavedArray) {
    const result = [];
    // For real FFT, we only use the first half (positive frequencies)
    // webfft.fftr() already returns only positive frequencies
    // Reverse the order to match the original frequency scale
    for (let i = interleavedArray.length - 2; i >= 0; i -= 2) {
        result.push([interleavedArray[i], interleavedArray[i + 1]]);
    }
    return result;
}

function logToLinear(logValue) {
    return (logValue - MIN_LOG_FREQ) / LOG_RANGE;
}

function linearToLog(linearValue) {
    return MIN_LOG_FREQ + (linearValue * LOG_RANGE);
}

function logToFrequency(logValue) {
    return Math.exp(logValue);
}

function frequencyToLog(frequency) {
    return Math.log(frequency);
}

// Window functions
const windowFunctions = {
    rectangular: (buffer) => {
        return buffer;
    },
    hann: (buffer) => {
        const N = buffer.length;
        const windowedBuffer = new Float32Array(N);
        for (let i = 0; i < N; i++) {
            windowedBuffer[i] = buffer[i] * 0.5 * (1 - Math.cos((2 * Math.PI * i) / (N - 1)));
        }
        return windowedBuffer;
    },
    hamming: (buffer) => {
        const N = buffer.length;
        const windowedBuffer = new Float32Array(N);
        for (let i = 0; i < N; i++) {
            windowedBuffer[i] = buffer[i] * (0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (N - 1)));
        }
        return windowedBuffer;
    },
    blackman: (buffer) => {
        const N = buffer.length;
        const windowedBuffer = new Float32Array(N);
        const alpha = 0.16;
        const a0 = (1 - alpha) / 2;
        const a1 = 0.5;
        const a2 = alpha / 2;
        for (let i = 0; i < N; i++) {
            windowedBuffer[i] = buffer[i] * (a0 - a1 * Math.cos((2 * Math.PI * i) / (N - 1)) + a2 * Math.cos((4 * Math.PI * i) / (N - 1)));
        }
        return windowedBuffer;
    }
};


// Color scheme functions
const colorSchemes = {
    rainbow: (value) => {
        const hue = 240 - (value / 255) * 240;
        return `hsl(${hue}, 100%, 50%)`;
    },
    'green-orange-red': (value) => {
        let r = 0, g = 0, b = 0;
        const n = value / 255; // Normalized value from 0 to 1

        if (n < 0.33) { // Black to Forest Green
            // Interpolate R, G, B from (0,0,0) to (34,139,34)
            r = Math.floor(n / 0.33 * 34);
            g = Math.floor(n / 0.33 * 139);
            b = Math.floor(n / 0.33 * 34);
        } else if (n < 0.66) { // Forest Green to Orange
            const n_phase = (n - 0.33) / 0.33;
            // Interpolate R from 34 to 255
            r = Math.floor(34 + n_phase * (255 - 34));
            // Interpolate G from 139 to 165
            g = Math.floor(139 + n_phase * (165 - 139));
            // Interpolate B from 34 to 0
            b = Math.floor(34 + n_phase * (0 - 34));
        } else { // Orange to Red
            const n_phase = (n - 0.66) / 0.34;
            // Interpolate R from 255 to 255 (stays 255)
            r = 255;
            // Interpolate G from 165 to 0
            g = Math.floor(165 + n_phase * (0 - 165));
            // Interpolate B from 0 to 0 (stays 0)
            b = 0;
        }
        return `rgb(${r}, ${g}, ${b})`;
    },
    grayscale: (value) => {
        const gray = Math.floor(value);
        return `rgb(${gray}, ${gray}, ${gray})`;
    }
};

let currentColorScheme = 'green-orange-red'; // Default color scheme
let isLogScale = true; // Default to logarithmic scale
let currentFftSize = 4096; // Default to 4096 FFT size
let currentWindowFunction = 'blackman'; // Default to Blackman window
let fftInstance; // WebFFT instance

// Optimization: Pre-computed frequency mappings
let frequencyMappings = [];
let frequencyBinMappings = [];
let colorCache = new Map();

let minFreqSlider;
let maxFreqSlider;
let minFreqDisplay;
let maxFreqDisplay;
let logScaleCheckbox;
let fftSizeSelect;
let sampleRateSelect;
let pauseResumeBtn;
let toggleControlsBtn;

function resizeCanvas() {
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    labelsCanvas.width = 40; // Fixed width for labels canvas
    labelsCanvas.height = labelsCanvas.clientHeight;
    cursorLineCanvas.width = canvas.clientWidth;
    cursorLineCanvas.height = canvas.clientHeight;

    // Resize time scale canvas
    if (timeScaleCanvas) {
        timeScaleCanvas.width = canvas.clientWidth;
        timeScaleCanvas.height = 40; // Fixed height for time scale
    }

    // Resize waveform canvas
    if (waveformCanvas && waveformArea) {
        waveformCanvas.width = canvas.clientWidth;
        // Use actual rendered height of waveform area
        waveformCanvas.height = waveformArea.clientHeight || waveformHeight;
    }

    // Update buffer size to match canvas width
    const newBufferSize = Math.max(canvas.width, 1000); // At least 1000 frames
    if (newBufferSize !== audioBufferSize) {
        console.log('Updating buffer size:', { old: audioBufferSize, new: newBufferSize });
        audioBufferSize = newBufferSize;
    }

    const displayWidth = canvas.clientWidth || canvas.width;
    lastTotalTimeSpan = displayWidth * getTimePerPixel();

    drawLabels(); // Redraw labels on resize
    drawTimeScale(); // Redraw time scale on resize
    updateFrequencyMappings(); // Recompute frequency mappings
}

function updateFrequencyMappings() {
    const canvasHeight = canvas.height;
    frequencyMappings = new Array(canvasHeight);
    frequencyBinMappings = new Array(canvasHeight);
    
    for (let i = 0; i < canvasHeight; i++) {
        let freq;
        if (isLogScale) {
            const logFreq = frequencyToLog(minFrequency) + ((canvasHeight - 1 - i) / (canvasHeight - 1)) * (frequencyToLog(maxFrequency) - frequencyToLog(minFrequency));
            freq = logToFrequency(logFreq);
        } else {
            freq = minFrequency + ((canvasHeight - 1 - i) / (canvasHeight - 1)) * (maxFrequency - minFrequency);
        }
        frequencyMappings[i] = freq;
        // Pre-compute frequency bin index - will be updated in draw() with actual FFT size
        frequencyBinMappings[i] = 0; // Placeholder, will be computed in draw()
    }
}

function initFftWorker() {
    try {
        fftWorker = new Worker(new URL('./fft-worker.js', import.meta.url), { type: 'module' });
        fftWorker.onmessage = function(e) {
            const { id, phasors, success, error } = e.data;
            
            if (success && pendingFftRequest && pendingFftRequest.id === id) {
                
                // Process the FFT result
                if (pendingFftRequest.isReprocessing) {
                    // Get the current index and calculate the X position
                    const currentIndex = pendingFftRequest.currentIndex || 0;
                    const xPosition = window.framePositions ? window.framePositions[currentIndex] : undefined;
                    // console.log('Web Worker completion - index:', currentIndex, 'xPosition:', xPosition, 'framePositions length:', window.framePositions ? window.framePositions.length : 'undefined');
                    processReprocessedFftResult(phasors, pendingFftRequest.fftSize, xPosition);
                    pendingFftRequest = null;
                    
                    // Continue with next frame in reprocessing
                    setTimeout(() => {
                        if (isReprocessing) {
                            // Continue reprocessing using the main processNextFrame function
                            if (currentIndex - 1 >= 0) {
                                window.processNextFrame(currentIndex - 1);
                            } else {
                                console.log('Reprocessing completed via Web Worker - processed', window.framePositions.length, 'frames');
                                if (window.reprocessingTimeout) {
                                    clearTimeout(window.reprocessingTimeout);
                                    window.reprocessingTimeout = null;
                                }
                                isReprocessing = false;
                                window.reprocessingColumn = undefined; // Reset column counter
                                window.reprocessingInProgress = false; // Clear the guard
                                window.reprocessingRetryCount = 0; // Reset retry counter
                                // Clear any remaining worker timeout
                                if (window.workerTimeout) {
                                    clearTimeout(window.workerTimeout);
                                    window.workerTimeout = null;
                                }
                            }
                        }
                    }, 0);
                } else {
                    processFftResult(phasors, pendingFftRequest.fftSize);
                    pendingFftRequest = null;
                }
            } else if (!success) {
                console.error('FFT Worker error:', error);
                pendingFftRequest = null;
            }
        };
        
        fftWorker.onerror = function(error) {
            console.error('FFT Worker error:', error);
            pendingFftRequest = null;
        };
    } catch (error) {
        console.error('Failed to initialize FFT Worker:', error);
        fftWorker = null;
    }
}

// Tear down the live audio graph so both the mic (OS-level recording indicator)
// and the AudioContext are released. Suspending the context alone does NOT turn
// off the mic indicator — the MediaStreamTrack has to be stop()ped.
function releaseAudioResources() {
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }
    if (mediaStream) {
        mediaStream.getTracks().forEach((track) => {
            try { track.stop(); } catch (_) { /* already stopped */ }
        });
        mediaStream = null;
    }
    if (audioContext) {
        audioContext.close().catch(() => { /* already closed */ });
        audioContext = null;
    }
    source = null;
    // Drop any pixels we'd accumulated while the old context was alive so we
    // don't paint a huge interpolated block across fresh content on resume.
    pendingShiftAmount = 0;
    previousFrameData = null;
}

async function restartAudioContext(sampleRate) {
    releaseAudioResources();
    await startAudio(sampleRate);
    drawTimeScale();
}

async function startAudio(sampleRate) {
    try {
        audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: sampleRate });
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // If the tab was hidden (or the user paused) while getUserMedia was
        // awaiting, drop the stream and bail so the mic indicator doesn't
        // light up for a background tab.
        if (isVisibilityHidden || isPaused) {
            stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
            try { await audioContext.close(); } catch (_) {}
            audioContext = null;
            return;
        }
        mediaStream = stream;
        source = audioContext.createMediaStreamSource(stream);
        await audioContext.audioWorklet.addModule(new URL('./audio-processor.js', import.meta.url));
        const audioWorkletNode = new AudioWorkletNode(audioContext, 'audio-processor');
        // One-second flatline threshold: if the mic feeds exact zeros for a
        // full second the OS has almost certainly routed us away, so we
        // restart the audio context to reacquire the stream.
        flatlineDetector = createFlatlineDetector({ thresholdSamples: sampleRate });
        audioWorkletNode.port.onmessage = (event) => {
            if (isPaused) return;
            const { buffer, timestamp } = event.data || {};
            if (!buffer || !buffer.length) return;

            // Append every incoming audio frame to the continuous buffer
            // here, on the audio callback, NOT inside draw(). draw() is
            // throttled by the FPS-limit slider, so doing the buffer write
            // there would drop intermediate audio frames (a 30 FPS draw
            // sees only every other ~11 ms audio buffer), leaving sample
            // discontinuities that produced wideband FFT smearing on loud
            // hits and visible jumps in the waveform trace.
            addToContinuousBuffer(buffer);

            // Surface the latest frame to draw() so it knows when to run
            // an FFT and what timestamp to attach. The continuous buffer is
            // already up to date even if draw() drops this signal.
            latestTimeDomainData = {
                buffer,
                timestamp: timestamp || Date.now(),
            };

            if (flatlineDetector && flatlineDetector.observe(buffer)) {
                const now = Date.now();
                if (!autoRestartInProgress
                    && now - lastAutoRestartAt > AUTO_RESTART_COOLDOWN_MS) {
                    autoRestartInProgress = true;
                    lastAutoRestartAt = now;
                    console.warn('Audio stream flatlined for 1s — restarting audio context');
                    const rate = sampleRateSelect
                        ? parseInt(sampleRateSelect.value)
                        : sampleRate;
                    restartAudioContext(rate).finally(() => {
                        autoRestartInProgress = false;
                    });
                }
            }
        };
        source.connect(audioWorkletNode).connect(audioContext.destination);
        await audioContext.resume(); // Explicitly resume the audio context

        

        // Initialize last processed frame data for frame skipping
        window.lastProcessedFrameData = null;
        smoothedTimePerPixel = 1 / BASE_ANIMATION_FPS;
        lastFrameTimestamp = null;
        lastDrawnTimePerPixel = smoothedTimePerPixel;
        lastTimeScaleUpdate = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const displayWidth = canvas ? (canvas.clientWidth || canvas.width) : 0;
        lastTotalTimeSpan = displayWidth * lastDrawnTimePerPixel;

        // Reset audio timing tracking
        audioBuffersProcessed = 0;
        audioTimingStartTime = null;

        // Reset FFT buffer tracking to avoid discontinuities during initialization
        window.lastFftBufferIndex = null;
        window.bufferStuckCounter = 0;

        // Initialize WebFFT instance
        try {
            fftInstance = new webfft(currentFftSize);
        } catch (error) {
            console.error('Failed to initialize WebFFT with size', currentFftSize, ':', error);
            throw error;
        }

        // Cancel any existing animation frame before starting a new one
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
        }
        draw();
    } catch (err) {
        console.error('Error accessing microphone:', err);
        alert('Error accessing microphone. Please allow microphone access and refresh the page.');
    }
}

function addToAudioBuffer(timeDomainData, windowFunction, timestamp) {
    // Add audio data to circular buffer
    const bufferEntry = {
        data: Array.from(timeDomainData),
        windowFunction: windowFunction,
        timestamp: timestamp || Date.now()
    };
    
    if (audioBuffer.length < audioBufferSize) {
        audioBuffer.push(bufferEntry);
    } else {
        audioBuffer[audioBufferIndex] = bufferEntry;
        audioBufferIndex = (audioBufferIndex + 1) % audioBufferSize;
    }
}

// Continuous buffer functions for temporal continuity
function initializeContinuousBuffer() {
    // Create a 15-second buffer at the current sample rate
    const sampleRate = audioContext ? audioContext.sampleRate : 44100;
    const bufferDurationSeconds = 15;
    continuousBufferSize = sampleRate * bufferDurationSeconds;
    continuousAudioBuffer = new Float32Array(continuousBufferSize);
    continuousBufferIndex = 0;
    continuousBufferFilled = false; // Reset filled flag
}

function addToContinuousBuffer(timeDomainData) {
    // Initialize buffer if not already done
    if (!continuousAudioBuffer) {
        initializeContinuousBuffer();
    }

    // Validate that we're actually receiving new data
    if (!timeDomainData || timeDomainData.length === 0) {
        console.warn('No audio data received in addToContinuousBuffer');
        return;
    }

    // Check for potentially stuck data (all zeros or constant values)
    let isConstant = true;
    const firstValue = timeDomainData[0];
    for (let i = 1; i < Math.min(10, timeDomainData.length); i++) {
        if (Math.abs(timeDomainData[i] - firstValue) > 1e-6) {
            isConstant = false;
            break;
        }
    }

    if (isConstant && Math.random() < 0.01) {
        console.warn('Potentially constant audio data detected:', firstValue);
    }

    // Add samples to the continuous circular buffer
    for (let i = 0; i < timeDomainData.length; i++) {
        const prevIndex = continuousBufferIndex;
        continuousAudioBuffer[continuousBufferIndex] = timeDomainData[i];
        continuousBufferIndex = (continuousBufferIndex + 1) % continuousBufferSize;

        // Detect when buffer has wrapped around (filled at least once)
        if (!continuousBufferFilled && continuousBufferIndex < prevIndex) {
            continuousBufferFilled = true;
        }
    }
}

function populateBuildInfo() {
    const commitEl = document.getElementById('build-commit');
    const dateEl = document.getElementById('build-date');
    const reloadBtn = document.getElementById('reload-btn');
    if (commitEl) {
        const short = /^[0-9a-f]{40}$/.test(BUILD_COMMIT) ? BUILD_COMMIT.slice(0, 7) : BUILD_COMMIT;
        commitEl.textContent = short;
        if (/^[0-9a-f]{40}$/.test(BUILD_COMMIT)) {
            commitEl.href = `https://github.com/jukkalaurila/spectroscope/commit/${BUILD_COMMIT}`;
        } else {
            commitEl.removeAttribute('href');
        }
    }
    if (dateEl && BUILD_DATE) {
        const d = new Date(BUILD_DATE);
        if (!isNaN(d.getTime())) {
            dateEl.textContent = ' · ' + d.toLocaleString();
        }
    }
    // Only expose a reload button on the dev preview — bypasses Cloudflare's
    // edge cache so fresh deploys show up without manual hard-refresh.
    if (reloadBtn && location.hostname.startsWith('dev.')) {
        reloadBtn.hidden = false;
        reloadBtn.addEventListener('click', () => location.reload());
    }

    if (location.hostname.startsWith('dev.')) {
        startDevAutoReloadPoller();
    }
}

function startDevAutoReloadPoller() {
    // Poll the static build-info.json emitted next to the bundle. When its
    // commit differs from the one we booted with, a newer deploy has landed
    // and we reload so the dev preview always shows the latest push. The
    // cache-busting query and no-store fetch defeat Cloudflare's edge cache
    // for this path. 1s interval balances a snappy refresh with minimal
    // background network chatter.
    setInterval(async () => {
        try {
            const resp = await fetch('/build-info.json?t=' + Date.now(), {
                cache: 'no-store',
            });
            if (!resp.ok) return;
            const { commit } = await resp.json();
            if (commit && commit !== BUILD_COMMIT) {
                console.info('New build detected, reloading:', commit.slice(0, 7));
                location.reload();
            }
        } catch (_) {
            // Network blip — try again on the next tick.
        }
    }, 1000);
}

function drawWaveform() {
    if (!waveformCanvas || !waveformCtx || !continuousAudioBuffer) {
        return;
    }
    if (!continuousBufferFilled && continuousBufferIndex === 0) {
        return;
    }

    const width = waveformCanvas.width;
    const height = waveformCanvas.height;
    const padding = 10;
    const drawableHeight = height - (padding * 2);
    const centerY = height / 2;

    // samplesInWindow is the number of audio samples the user has chosen to
    // see; width is the pixel width of the canvas. At zoom=1 they match
    // (one sample per pixel, original behaviour). Zooming in shows fewer
    // samples stretched across the same pixel width; zooming out shows more.
    const samplesInWindow = Math.max(
        MIN_WAVEFORM_SAMPLES,
        Math.min(MAX_WAVEFORM_SAMPLES, Math.round(width * waveformTimeZoom))
    );
    const samplesNeeded = samplesInWindow * 2;
    const available = continuousBufferFilled ? continuousBufferSize : continuousBufferIndex;
    if (available < samplesNeeded) {
        return;
    }

    // Read the most recent samples from the circular buffer
    const samples = new Float32Array(samplesNeeded);
    for (let i = 0; i < samplesNeeded; i++) {
        let idx = continuousBufferIndex - samplesNeeded + i;
        if (idx < 0) idx += continuousBufferSize;
        samples[i] = continuousAudioBuffer[idx];
    }

    // Find optimal trigger point using cross-correlation with previous waveform
    let triggerIndex = 0;
    const searchLimit = samplesNeeded - samplesInWindow;
    const hasPrevious = previousWaveform && previousWaveform.length === samplesInWindow;
    const windowMid = Math.floor(samplesInWindow / 2);
    const crossingSearchRadius = Math.max(1, Math.floor(samplesInWindow / 4));
    const crossingAlpha = waveformAnchorWeight;

    // Pitch-locked anchor: estimate the fundamental period of the buffered
    // audio, fit a single sine at that frequency, and use that sine's
    // upward zero-crossings as the alignment target. Unlike raw-sample
    // crossings these are evenly spaced at exactly `period` samples, so
    // a harmonic-rich or noisy signal still has one clear centering point
    // per period instead of several near-misses.
    let anchorPeriod = null;
    let anchorFirst = 0;
    if (audioContext && audioContext.sampleRate) {
        const pitchHz = detectPitchHz(samples, { sampleRate: audioContext.sampleRate });
        if (pitchHz) {
            const period = audioContext.sampleRate / pitchHz;
            const omega = (2 * Math.PI) / period;
            const phase = fitSinePhase(samples, omega);
            if (phase !== null) {
                // Upward zero of sin(ω·i + φ) is at i = -φ/ω (mod period).
                let first = -phase / omega;
                first = ((first % period) + period) % period;
                anchorPeriod = period;
                anchorFirst = first;
            }
        }
    }

    // Since the fitted-sine crossings form an arithmetic progression
    // (first + k·period), the nearest crossing to any index c is a single
    // Math.round computation — O(1) per candidate offset.
    const alignmentBonusFor = (centre) => {
        if (!anchorPeriod) return 0;
        const nearest = anchorFirst + anchorPeriod * Math.round((centre - anchorFirst) / anchorPeriod);
        const dist = Math.abs(centre - nearest);
        return Math.max(0, 1 - dist / (crossingSearchRadius + 1));
    };

    if (hasPrevious) {
        let bestScore = -Infinity;
        const step = Math.max(1, Math.floor(samplesInWindow / 128));
        for (let offset = 0; offset < searchLimit; offset++) {
            let correlation = 0;
            for (let i = 0; i < samplesInWindow; i += step) {
                correlation += samples[offset + i] * previousWaveform[i];
            }
            const score = correlation * (1 + crossingAlpha * alignmentBonusFor(offset + windowMid));
            if (score > bestScore) {
                bestScore = score;
                triggerIndex = offset;
            }
        }
    } else if (anchorPeriod) {
        // First frame with a confident pitch: place the fitted-sine upward
        // zero-crossing closest to the middle of the search range at the
        // displayed centre.
        const targetCentre = Math.floor(searchLimit / 2) + windowMid;
        const nearest = anchorFirst + anchorPeriod * Math.round((targetCentre - anchorFirst) / anchorPeriod);
        triggerIndex = Math.max(0, Math.min(searchLimit - 1, Math.round(nearest) - windowMid));
    } else {
        // No previous frame, no confident pitch: fall back to the first raw
        // upward zero-crossing.
        for (let i = 1; i < searchLimit; i++) {
            if (samples[i - 1] < 0 && samples[i] >= 0) {
                triggerIndex = i;
                break;
            }
        }
    }

    // Pick the gain for this frame. See chooseWaveformGain in utils.js
    // for the reasoning behind the peak + √2·RMS target.
    const windowSamples = samples.subarray(triggerIndex, triggerIndex + samplesInWindow);
    const gainResult = chooseWaveformGain(
        windowSamples,
        hasPrevious ? previousWaveform : null,
        previousGainLevel,
        waveformGainLevel,
    );
    waveformGainLevel = gainResult.gain;

    const maxAmplitude = Math.max(waveformGainLevel, 0.001);

    // Phosphor fade or clear
    const now = performance.now();
    const elapsed = now - lastWaveformDrawTime;
    lastWaveformDrawTime = now;

    if (phosphorDecayMs > 0 && elapsed > 0) {
        // Fade previous content: overlay black with opacity based on elapsed time and decay
        const fadeAlpha = Math.min(1, elapsed / phosphorDecayMs);
        waveformCtx.fillStyle = `rgba(0, 0, 0, ${fadeAlpha})`;
        waveformCtx.fillRect(0, 0, width, height);
    } else {
        waveformCtx.fillStyle = '#000';
        waveformCtx.fillRect(0, 0, width, height);
    }

    waveformCtx.strokeStyle = '#00ff00';
    waveformCtx.lineWidth = 1;
    waveformCtx.beginPath();
    // Map each canvas pixel to a (fractional) sample within the zoomed
    // window and linearly interpolate between neighbouring samples so the
    // trace is a smooth ramp rather than a staircase when zoomed in.
    const sampleStep = samplesInWindow / width;
    const lastIdx = samplesInWindow - 1;
    for (let x = 0; x < width; x++) {
        const fIdx = x * sampleStep;
        const i0 = Math.floor(fIdx);
        const i1 = i0 < lastIdx ? i0 + 1 : lastIdx;
        const frac = fIdx - i0;
        const sample = windowSamples[i0] * (1 - frac) + windowSamples[i1] * frac;
        const y = centerY - (sample / maxAmplitude) * (drawableHeight / 2);
        if (x === 0) {
            waveformCtx.moveTo(x, y);
        } else {
            waveformCtx.lineTo(x, y);
        }
    }
    waveformCtx.stroke();

    // Store this waveform and the gain it was drawn with for next frame's
    // correlation trigger and overlap-based scale selection.
    previousWaveform = samples.slice(triggerIndex, triggerIndex + samplesInWindow);
    previousGainLevel = maxAmplitude;

    // Draw center line
    waveformCtx.strokeStyle = '#333';
    waveformCtx.lineWidth = 1;
    waveformCtx.globalAlpha = 0.5;
    waveformCtx.beginPath();
    waveformCtx.moveTo(0, centerY);
    waveformCtx.lineTo(width, centerY);
    waveformCtx.stroke();
    waveformCtx.globalAlpha = 1;

    // Label the window duration with a ruler-style scale spanning the full
    // width: tick marks at both ends, a horizontal line, and the label
    // centred in the middle.
    if (audioContext && audioContext.sampleRate) {
        const durationMs = (samplesInWindow / audioContext.sampleRate) * 1000;
        const label = durationMs >= 100
            ? `${durationMs.toFixed(0)} ms`
            : `${durationMs.toFixed(1)} ms`;
        const yLine = 10;
        const tickHalf = 4;
        const padding = 6;
        const midX = width / 2;

        waveformCtx.strokeStyle = '#888';
        waveformCtx.fillStyle = '#888';
        waveformCtx.lineWidth = 1;
        waveformCtx.font = '12px monospace';
        waveformCtx.textAlign = 'center';
        waveformCtx.textBaseline = 'middle';

        const textWidth = waveformCtx.measureText(label).width;
        const leftGap = midX - textWidth / 2 - padding;
        const rightGap = midX + textWidth / 2 + padding;

        waveformCtx.beginPath();
        // Left segment
        waveformCtx.moveTo(0, yLine);
        waveformCtx.lineTo(Math.max(0, leftGap), yLine);
        // Right segment
        waveformCtx.moveTo(Math.min(width, rightGap), yLine);
        waveformCtx.lineTo(width - 1, yLine);
        // End tick marks (0.5 offsets keep 1px strokes crisp)
        waveformCtx.moveTo(0.5, yLine - tickHalf);
        waveformCtx.lineTo(0.5, yLine + tickHalf);
        waveformCtx.moveTo(width - 0.5, yLine - tickHalf);
        waveformCtx.lineTo(width - 0.5, yLine + tickHalf);
        waveformCtx.stroke();

        waveformCtx.fillText(label, midX, yLine);
    }
}

function detectPitch() {
    if (!continuousAudioBuffer || !audioContext || !audioContext.sampleRate) {
        currentFundamentalHz = null;
        return;
    }
    const available = continuousBufferFilled ? continuousBufferSize : continuousBufferIndex;
    const analysisLen = Math.min(PITCH_ANALYSIS_SAMPLES, available);
    if (analysisLen < 512) {
        currentFundamentalHz = null;
        return;
    }
    const buf = new Float32Array(analysisLen);
    for (let i = 0; i < analysisLen; i++) {
        let idx = continuousBufferIndex - analysisLen + i;
        if (idx < 0) idx += continuousBufferSize;
        buf[i] = continuousAudioBuffer[idx];
    }
    currentFundamentalHz = detectPitchHz(buf, { sampleRate: audioContext.sampleRate });
}

// Measure the full-size label width for `noteName`, including the backdrop
// padding. Mutates ctx.font; callers should reset afterwards if needed.
function fullSizeLabelWidth(noteName) {
    ctx.font = `bold ${NOTE_LABEL_SIZES[0]}px monospace`;
    return ctx.measureText(noteName).width + NOTE_LABEL_PAD_X + 1;
}

// Write the pending note label to the canvas, sized to the largest font that
// fits inside `availableWidth` (pass Infinity to force the largest size).
function commitPendingNoteLabel(availableWidth) {
    if (!pendingNoteLabel || !canvas || !ctx) {
        pendingNoteLabel = null;
        return;
    }
    const { noteName, anchorX, lineY } = pendingNoteLabel;
    const size = pickLabelFontSize(
        availableWidth,
        NOTE_LABEL_SIZES,
        (s) => {
            ctx.font = `bold ${s}px monospace`;
            return ctx.measureText(noteName).width;
        },
        NOTE_LABEL_PAD_X + 1,
    );
    ctx.font = `bold ${size}px monospace`;
    const textWidth = ctx.measureText(noteName).width;
    const bgHeight = size + 2;
    const bgWidth = textWidth + NOTE_LABEL_PAD_X + 1;
    // Anchor the label at the onset column, flowing rightward across the
    // duration of the note. Clamp to the canvas so an onset that has
    // nearly scrolled off the left still renders something legible.
    const bgX = Math.max(0, Math.min(canvas.width - bgWidth, Math.round(anchorX)));
    const bgY = Math.max(0, Math.min(canvas.height - bgHeight, lineY + 3));
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(bgX, bgY, bgWidth, bgHeight);
    ctx.fillStyle = '#00ffff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(noteName, bgX + 1, bgY + 1);
    pendingNoteLabel = null;
}

// Paint the cyan fundamental-frequency marker onto the spectrogram's newly
// drawn columns, and maintain a deferred note-onset label. Labels are held
// back until we know how long the note lasts: a note that persists long
// enough to fit a full-size label gets one; a note cut short by the next
// onset gets the largest size that fits in the gap. The marker and label
// are painted directly to the spectrogram canvas so the scroll on the next
// frame carries them left, producing a pitch trace and a timeline of
// detected notes.
function paintPitchOverlay(xStart, xWidth) {
    if (!canvas || !ctx) return;
    const h = canvas.height;

    // Age the pending onset by the pixels the canvas just scrolled. This
    // must run every call (including ones with no detected pitch) so the
    // anchor x and elapsed width stay in sync with on-canvas pixels.
    if (pendingNoteLabel) {
        pendingNoteLabel.anchorX -= xWidth;
        pendingNoteLabel.elapsedWidth += xWidth;
        // If the onset has scrolled fully off the left edge without ever
        // being committed (e.g. pitch dropped out immediately after), drop it.
        if (pendingNoteLabel.anchorX + fullSizeLabelWidth(pendingNoteLabel.noteName) < 0) {
            pendingNoteLabel = null;
        }
    }

    if (currentFundamentalHz === null
        || currentFundamentalHz < minFrequency
        || currentFundamentalHz > maxFrequency) {
        return;
    }
    const rawY = getYFromFrequencyPure(currentFundamentalHz, h, minFrequency, maxFrequency, isLogScale);
    if (rawY < 0 || rawY > h) return;
    const lineY = Math.round(rawY);

    ctx.fillStyle = '#00ffff';
    // 2px tall; clamp so we don't draw past the bottom edge of the canvas.
    const lineHeight = 2;
    const lineTop = Math.min(lineY, h - lineHeight);
    ctx.fillRect(xStart, lineTop, xWidth, lineHeight);

    const noteName = frequencyToNote(currentFundamentalHz)?.name;
    if (!noteName) return;

    if (noteName !== lastDetectedNoteName) {
        // New onset. Commit whatever was pending with the elapsed width as
        // its budget (short notes → small labels), then start tracking the
        // new note. The new pending label is drawn only once its duration
        // is known.
        if (pendingNoteLabel) {
            commitPendingNoteLabel(pendingNoteLabel.elapsedWidth);
        }
        pendingNoteLabel = {
            noteName,
            anchorX: xStart,
            lineY,
            elapsedWidth: 0,
        };
        lastDetectedNoteName = noteName;
        return;
    }

    // Same note as before. Commit at full size the moment enough elapsed
    // width has accumulated to fit one. Long, sustained notes all go through
    // here once; further paints are no-ops for the label logic.
    if (pendingNoteLabel && pendingNoteLabel.elapsedWidth >= fullSizeLabelWidth(pendingNoteLabel.noteName)) {
        commitPendingNoteLabel(Infinity);
    }
    // Deliberately keep lastDetectedNoteName when the pitch drops out for a
    // frame or two — brief gaps in pitch detection shouldn't re-trigger the
    // same note label when the sound returns.
}

function processFftResult(phasors, fftSize) {
    // Update frequency bin mappings with actual FFT size
    const freqBinWidth = audioContext.sampleRate / fftSize;
    for (let i = 0; i < frequencyMappings.length; i++) {
        frequencyBinMappings[i] = Math.floor(frequencyMappings[i] / freqBinWidth);
    }

    // Process the FFT data and store it for potential reuse
    const frameData = processFftData(phasors, fftSize);
    window.lastProcessedFrameData = frameData;

    // Paint the pending gap by interpolating from the previous column to this
    // new one — closes over all the pixels the canvas has scrolled since the
    // last FFT response without leaving stripes of old data between columns.
    const width = Math.max(1, pendingShiftAmount);
    const left = previousFrameData || frameData;
    paintRightEdgeInterpolated(left, frameData, width);
    previousFrameData = frameData;
    pendingShiftAmount = 0;
}

// Paint `width` columns at the right edge of the spectrogram, linearly
// interpolating from `left` to `right` in 0-255 magnitude space. The rightmost
// column is `right` (t = 1); the column nearest the existing content is closer
// to `left`, so the gap blends into the new data rather than showing a stripe.
function paintRightEdgeInterpolated(left, right, width) {
    if (!canvas || width < 1) return;
    const canvasWidth = canvas.width;
    if (left === right || !left) {
        for (let i = 0; i < width; i++) {
            drawFrameData(right, canvasWidth - width + i);
        }
    } else {
        const canvasHeight = canvas.height;
        const blended = new Array(canvasHeight);
        for (let i = 0; i < width; i++) {
            const t = (i + 1) / width;
            for (let j = 0; j < canvasHeight; j++) {
                blended[j] = Math.round(left[j] * (1 - t) + right[j] * t);
            }
            drawFrameData(blended, canvasWidth - width + i);
        }
    }
    paintPitchOverlay(canvasWidth - width, width);
}

function processFftData(phasors, fftSize) {
    const canvasHeight = canvas.height;
    const minDb = document.getElementById('min-db') ? parseInt(document.getElementById('min-db').value) : -100;
    const maxDb = document.getElementById('max-db') ? parseInt(document.getElementById('max-db').value) : -20;
    
    const frameData = new Array(canvasHeight);
    
    for (let i = 0; i < canvasHeight; i++) {
        const freqIndex = frequencyBinMappings[i];

        // Ensure freqIndex is within bounds of the FFT output
        let value = 0; // Initialize value outside the if block
        if (freqIndex >= 0 && freqIndex < phasors.length) {
            // Calculate magnitude from complex FFT output
            const real = phasors[freqIndex][0];
            const imag = phasors[freqIndex][1];
            const magnitude = Math.sqrt(real * real + imag * imag);
            
            // Normalize by FFT size to get proper scaling
            const normalizedMagnitude = magnitude / fftSize;
            
            // Convert magnitude to decibels with proper reference
            value = 20 * Math.log10(normalizedMagnitude + 1e-10); // Add small epsilon to avoid log(0)
            
            // Normalize decibel value to 0-255 range for coloring
            value = Math.max(minDb, Math.min(maxDb, value)); // Clamp value within min/max dB
            value = Math.floor(((value - minDb) / (maxDb - minDb)) * 255); // Normalize to 0-255
        } else {
            // Out of bounds - set to minimum value
            value = 0;
        }

        frameData[i] = value;
    }
    
    return frameData;
}

function drawFrameData(frameData, xPosition) {
    const canvasHeight = canvas.height;
    
    // Batch canvas operations for better performance
    ctx.beginPath();
    
    for (let i = 0; i < canvasHeight; i++) {
        const value = frameData[i];

        // Cache color lookups for better performance
        let color = colorCache.get(value);
        if (color === undefined) {
            color = colorSchemes[currentColorScheme](value);
            colorCache.set(value, color);
        }

        // Use the selected color scheme
        ctx.fillStyle = color;

        // Draw a 1px-wide rectangle at the specified position
        ctx.fillRect(xPosition, i, 1, 1);
    }
}

function reprocessAudioBuffer() {
    console.log('Reprocessing audio buffer...', { 
        isReprocessing, 
        bufferLength: audioBuffer.length, 
        currentFftSize, 
        currentWindowFunction 
    });
    
    if (audioBuffer.length === 0) {
        console.log('Skipping reprocessing: buffer is empty');
        return;
    }
    
    // If already reprocessing, cancel it and start fresh
    if (isReprocessing || window.reprocessingInProgress) {
        console.log('Cancelling ongoing reprocessing and starting fresh');
        
        // Cancel any ongoing reprocessing
        isReprocessing = false;
        window.reprocessingInProgress = false;
        window.reprocessingRetryCount = 0;
        pendingFftRequest = null;
        
        // Clear any timeouts
        if (window.workerTimeout) {
            clearTimeout(window.workerTimeout);
            window.workerTimeout = null;
        }
        
        // Clear any main reprocessing timeout
        if (window.reprocessingTimeout) {
            clearTimeout(window.reprocessingTimeout);
            window.reprocessingTimeout = null;
        }
        
        // Reset column counter
        window.reprocessingColumn = undefined;
        
        // Small delay to ensure cleanup is complete
        setTimeout(() => {
            reprocessAudioBuffer();
        }, 10);
        return;
    }
    
    window.reprocessingInProgress = true;
    window.reprocessingRetryCount = 0; // Reset retry counter
    
    isReprocessing = true;
    
    // Always process from right to left, but calculate exact X positions for each frame
    window.reprocessingColumn = canvas.width - 1;
    
    // Calculate the original X positions for each buffer entry
    // Each frame shifts the canvas left by 1 pixel, so older frames are further left
    const bufferToProcess = [...audioBuffer];
    window.framePositions = []; // Make globally accessible
    window.frameIndices = []; // Store the corresponding buffer indices
    
    // When paused, we only process frames that actually have data
    // When live, we only process the frames that are actually in the buffer
    const maxFrames = bufferToProcess.length;
    
    // Build arrays in screen order (left to right, oldest to newest)
    for (let i = 0; i < maxFrames; i++) {
        // Calculate the position - frames go from left to right
        // Account for scrolling speed: each frame shifts by scrollingSpeed pixels
        const shiftAmount = Math.max(1, Math.round(scrollingSpeed));
        const xPos = Math.max(0, canvas.width - (maxFrames * shiftAmount) + (i * shiftAmount));
        
        // Calculate the buffer index based on how the frames appear on screen
        let bufferIndex;
        if (audioBuffer.length < audioBufferSize) {
            // Buffer not full yet - frames are in chronological order
            bufferIndex = i;
        } else {
            // Buffer is full - use circular buffer logic
            // The oldest frame (left edge) is at audioBufferIndex
            // The newest frame (right edge) is at audioBufferIndex - 1
            bufferIndex = (audioBufferIndex + i) % audioBufferSize;
        }
        
        window.framePositions.push(xPos);
        window.frameIndices.push(bufferIndex); // Store the actual buffer index
    }
    
    console.log('Reprocessing with calculated frame positions:', window.framePositions.slice(0, 10), '...');
    console.log('Canvas width:', canvas.width, 'Buffer length:', bufferToProcess.length);
    console.log('First few positions:', window.framePositions.slice(0, 5));
    console.log('Last few positions:', window.framePositions.slice(-5));
    console.log('Processing order - will start from index:', window.framePositions.length - 1, 'and go to 0');
    console.log('This means starting from position:', window.framePositions[window.framePositions.length - 1], 'and ending at position:', window.framePositions[0]);
    
    // Set a timeout to ensure reprocessing flag doesn't get stuck
    // Longer timeout for large FFTs - use the actual current FFT size
    const actualFftSize = currentFftSize;
    const timeoutMs = actualFftSize >= 8192 ? 60000 : 20000; // Increased timeouts
    console.log('Setting reprocessing timeout:', timeoutMs, 'ms for FFT size:', actualFftSize);
    window.reprocessingTimeout = setTimeout(() => {
        if (isReprocessing) {
            console.log('Reprocessing timeout - resetting flag after', timeoutMs, 'ms for FFT size:', actualFftSize);
            isReprocessing = false;
            window.reprocessingInProgress = false; // Clear the guard
            pendingFftRequest = null; // Clear any stuck Web Worker request
            // Clear any worker timeout
            if (window.workerTimeout) {
                clearTimeout(window.workerTimeout);
                window.workerTimeout = null;
            }
        }
    }, timeoutMs);
    
    console.log('Reprocessing buffer:', { 
        bufferFrames: bufferToProcess.length, 
        canvasWidth: canvas.width,
        bufferSize: audioBufferSize,
        bufferIndex: audioBufferIndex
    });
    

    
    // Start processing from the last frame (right edge) and work backwards
    window.processNextFrame(window.framePositions.length - 1);
}

// Move processNextFrame outside to make it globally accessible
window.processNextFrame = function(index) {
    // Process all frames needed to fill the canvas
    const framesToProcess = window.framePositions.length;
    
    // Log progress every 100 frames
    if (index % 100 === 0) {
        console.log('Reprocessing progress:', index, '/', framesToProcess, 'frames');
    }
    
    if (index < 0) {
        if (window.reprocessingTimeout) {
            clearTimeout(window.reprocessingTimeout);
            window.reprocessingTimeout = null;
        }
        isReprocessing = false;
        window.reprocessingColumn = undefined; // Reset column counter
        window.reprocessingInProgress = false; // Clear the guard
        window.reprocessingRetryCount = 0; // Reset retry counter
        // Clear any worker timeout
        if (window.workerTimeout) {
            clearTimeout(window.workerTimeout);
            window.workerTimeout = null;
        }
        console.log('Reprocessing completed - processed', framesToProcess, 'frames out of', audioBuffer.length, 'available');
        return;
    }
    
    // Log the first frame to see if processing starts
    if (index === framesToProcess - 1) {
        console.log('Starting reprocessing of first frame (right edge)');
    }
    
    // Use the frame index from our mapping (processes from right to left)
    const frameIndex = window.frameIndices[index];
    const frame = audioBuffer[frameIndex];
    // Use the current FFT size setting, not the stored one
    const fftSize = Math.pow(2, Math.floor(Math.log2(currentFftSize)));

    // Copy data and pad/truncate as needed
    const fftInput = new Array(fftSize);
    for (let i = 0; i < fftSize; i++) {
        fftInput[i] = frame.data[i] || 0;
    }
    
    // Use Web Worker for FFT computation
    if (fftWorker && !pendingFftRequest) {
        if (index === 0) {
            console.log('Using Web Worker for reprocessing');
        }
        const requestId = ++fftRequestId;
        pendingFftRequest = { id: requestId, fftSize: fftSize, isReprocessing: true, currentIndex: index };
        
        // Set a timeout for individual Web Worker requests
        window.workerTimeout = setTimeout(() => {
            if (pendingFftRequest && pendingFftRequest.id === requestId) {
                console.log('Web Worker request timeout, falling back to synchronous FFT');
                pendingFftRequest = null;
                // Fall back to synchronous processing
                try {
                    const interleavedResult = fftInstance.fftr(fftInput);
                    const phasors = convertWebfftOutput(interleavedResult);
                    processReprocessedFftResult(phasors, fftSize, window.framePositions[index]);
                    setTimeout(() => window.processNextFrame(index - 1), 0);
                } catch (error) {
                    console.error('Error in fallback FFT processing:', error);
                    setTimeout(() => window.processNextFrame(index - 1), 0);
                }
            }
        }, 10000); // Increased to 10 second timeout for individual requests
        
        fftWorker.postMessage({
            id: requestId,
            data: fftInput,
            windowFunction: currentWindowFunction
        });
    } else {
        // Fallback to synchronous FFT
        if (index === 0) {
            console.log('Using synchronous FFT fallback for reprocessing - fftWorker:', !!fftWorker, 'pendingFftRequest:', !!pendingFftRequest);
        }
        try {
            const interleavedResult = fftInstance.fftr(fftInput);
            const phasors = convertWebfftOutput(interleavedResult);
            processReprocessedFftResult(phasors, fftSize, window.framePositions[index]);
            // Use shorter delay for large FFTs to avoid timeout
            const delay = currentFftSize >= 8192 ? 1 : 1; // Small delay to prevent overwhelming
            setTimeout(() => window.processNextFrame(index - 1), delay);
        } catch (error) {
            console.error('Error in synchronous FFT processing:', error);
            // Continue to next frame even if this one fails
            setTimeout(() => window.processNextFrame(index - 1), 0);
        }
    }
};

function processNextReprocessingFrame(index, bufferToProcess, framePositions) {
    const framesToProcess = framePositions.length;
    if (index >= framesToProcess) {
        console.log('Reprocessing completed via synchronous FFT - processed', framesToProcess, 'frames');
        if (window.reprocessingTimeout) {
            clearTimeout(window.reprocessingTimeout);
            window.reprocessingTimeout = null;
        }
        isReprocessing = false;
        window.reprocessingColumn = undefined; // Reset column counter
        window.reprocessingInProgress = false; // Clear the guard
        window.reprocessingRetryCount = 0; // Reset retry counter
        // Clear any worker timeout
        if (window.workerTimeout) {
            clearTimeout(window.workerTimeout);
            window.workerTimeout = null;
        }
        return;
    }
    
    // Use the frame index from our mapping (processes from right to left)
    const frameIndex = window.frameIndices[index];
    const frame = bufferToProcess[frameIndex];
    // Use the current FFT size setting, not the stored one
    const fftSize = Math.pow(2, Math.floor(Math.log2(currentFftSize)));

    // Copy data and pad/truncate as needed
    const fftInput = new Array(fftSize);
    for (let i = 0; i < fftSize; i++) {
        fftInput[i] = frame.data[i] || 0;
    }
    
    // Use Web Worker for FFT computation
    if (fftWorker && !pendingFftRequest) {
        const requestId = ++fftRequestId;
        pendingFftRequest = { 
            id: requestId, 
            fftSize: fftSize, 
            isReprocessing: true,
            currentIndex: index
        };
        
        // Set a timeout for this individual Web Worker request
        window.workerTimeout = setTimeout(() => {
            if (pendingFftRequest && pendingFftRequest.id === requestId) {
                console.log('Web Worker request timeout in reprocessing, falling back to synchronous FFT');
                pendingFftRequest = null;
                // Fall back to synchronous processing
                try {
                    const interleavedResult = fftInstance.fftr(fftInput);
                    const phasors = convertWebfftOutput(interleavedResult);
                    processReprocessedFftResult(phasors, fftSize, window.framePositions[index]);
                    setTimeout(() => processNextReprocessingFrame(index + 1, bufferToProcess, window.framePositions), 0);
                } catch (error) {
                    console.error('Error in fallback FFT processing during reprocessing:', error);
                    setTimeout(() => processNextReprocessingFrame(index + 1, bufferToProcess, window.framePositions), 0);
                }
            }
        }, 10000); // 10 second timeout for individual requests
        
        fftWorker.postMessage({
            id: requestId,
            data: fftInput,
            windowFunction: currentWindowFunction
        });
    } else {
        // Fallback to synchronous FFT
        const interleavedResult = fftInstance.fftr(fftInput);
        const phasors = convertWebfftOutput(interleavedResult);
        processReprocessedFftResult(phasors, fftSize, window.framePositions[index]);
        // Use shorter delay for large FFTs to avoid timeout
        const delay = currentFftSize >= 8192 ? 1 : 1; // Small delay to prevent overwhelming
        setTimeout(() => processNextReprocessingFrame(index + 1, bufferToProcess, window.framePositions), delay);
    }
}

function processReprocessedFftResult(phasors, fftSize, xPosition) {
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Update frequency bin mappings with actual FFT size
    const freqBinWidth = audioContext.sampleRate / fftSize;
    for (let i = 0; i < frequencyMappings.length; i++) {
        frequencyBinMappings[i] = Math.floor(frequencyMappings[i] / freqBinWidth);
    }

    // Use the provided X position for drawing
    const drawX = xPosition;
    
    // Debug logging (commented out since it's working)
    // console.log('Drawing reprocessed frame at X position:', drawX, 'FFT size:', fftSize);

    // Draw the spectrogram slice at the current column position
    const minDb = document.getElementById('min-db') ? parseInt(document.getElementById('min-db').value) : -100;
    const maxDb = document.getElementById('max-db') ? parseInt(document.getElementById('max-db').value) : -20;
    
    // Batch canvas operations for better performance
    ctx.beginPath();
    
    // Set global alpha for reprocessing to make it slightly transparent
    ctx.globalAlpha = 0.9; // Slightly transparent to distinguish from original
    
    // Account for scrolling speed when drawing reprocessed frames
    const shiftAmount = Math.max(1, Math.round(scrollingSpeed));
    
    for (let i = 0; i < canvasHeight; i++) {
        const freqIndex = frequencyBinMappings[i];

        // Ensure freqIndex is within bounds of the FFT output
        let value = 0; // Initialize value outside the if block
        if (freqIndex >= 0 && freqIndex < phasors.length) {
            // Calculate magnitude from complex FFT output
            const real = phasors[freqIndex][0];
            const imag = phasors[freqIndex][1];
            const magnitude = Math.sqrt(real * real + imag * imag);
            
            // Normalize by FFT size to get proper scaling
            const normalizedMagnitude = magnitude / fftSize;
            
            // Convert magnitude to decibels with proper reference
            value = 20 * Math.log10(normalizedMagnitude + 1e-10); // Add small epsilon to avoid log(0)
            
            // Normalize decibel value to 0-255 range for coloring
            value = Math.max(minDb, Math.min(maxDb, value)); // Clamp value within min/max dB
            value = Math.floor(((value - minDb) / (maxDb - minDb)) * 255); // Normalize to 0-255
        } else {
            // Out of bounds - set to minimum value
            value = 0;
        }

        // Cache color lookups for better performance
        let color = colorCache.get(value);
        if (color === undefined) {
            color = colorSchemes[currentColorScheme](value);
            colorCache.set(value, color);
        }

        // Use the selected color scheme
        ctx.fillStyle = color;

        // Draw across multiple pixels based on scrolling speed
        for (let j = 0; j < shiftAmount; j++) {
            ctx.fillRect(drawX + j, i, 1, 1);
        }
    }
    
    // Reset global alpha
    ctx.globalAlpha = 1.0;
}

function draw() {
    const timeDomainFrame = latestTimeDomainData;
    if (isPaused || isVisibilityHidden) {
        return;
    }

    // Skip live updates if reprocessing is in progress
    if (isReprocessing || window.reprocessingInProgress) {
        // Still continue the animation loop but don't process new frames
        animationFrameId = requestAnimationFrame(draw);
        return;
    }

    const currentTime = performance.now();
    if (currentTime - lastDrawBodyTime < targetDrawIntervalMs - DRAW_INTERVAL_CUSHION_MS) {
        animationFrameId = requestAnimationFrame(draw);
        return;
    }
    lastDrawBodyTime = currentTime;

    animationFrameId = requestAnimationFrame(draw);

    // Refresh pitch from the continuous buffer before any spectrogram
    // painting, so paintPitchOverlay() writes an up-to-date marker and
    // note-onset label onto the new column.
    detectPitch();

    // FPS calculation
    fpsCounter++;
    
    if (currentTime - fpsLastTime >= 1000) { // Update FPS every second
        if (fpsDisplay) {
            fpsDisplay.textContent = fpsCounter;
        }
        fpsCounter = 0;
        fpsLastTime = currentTime;
    }
    
    // Advance the scroll accumulator by real elapsed time. scrollingSpeed is
    // pixels per second (scaled so 1.0 == SCROLL_REFERENCE_FPS px/s) regardless
    // of how often draw() actually runs, so the spectrogram scrolls at the
    // same wall-clock rate whether the FPS cap is 20 or 120.
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    let deltaSeconds = 0;
    if (lastFrameTimestamp !== null) {
        deltaSeconds = (currentTime - lastFrameTimestamp) / 1000;
    }

    if (deltaSeconds > 0 && deltaSeconds < 1) {
        scrollAccumulator += scrollingSpeed * SCROLL_REFERENCE_FPS * deltaSeconds;
    }

    const shiftAmount = Math.floor(scrollAccumulator);
    if (shiftAmount < 1) {
        // Not enough elapsed time for a full pixel yet — keep the loop running
        // but don't paint, process audio, or advance the time-per-pixel estimate.
        lastFrameTimestamp = currentTime;
        return;
    }
    scrollAccumulator -= shiftAmount;

    if (deltaSeconds > 0 && deltaSeconds < 1) {
        const instantaneousTimePerPixel = Math.max(deltaSeconds / shiftAmount, MIN_TIME_PER_PIXEL);
        // Apply compound correction factor: (9/9.17) * (10/10.97) * (10/10.22)
        const correctedTimePerPixel = instantaneousTimePerPixel * (9.0 / 9.17) * (10.0 / 10.97) * (10.0 / 10.22);
        const alpha = TIME_PER_PIXEL_SMOOTHING;
        smoothedTimePerPixel = (smoothedTimePerPixel * (1 - alpha)) + (correctedTimePerPixel * alpha);
    }

    if (Math.abs(smoothedTimePerPixel - lastDrawnTimePerPixel) > 0.001 && timeScaleCtx) {
        const nowMs = currentTime;
        if (nowMs - lastTimeScaleUpdate >= TIME_SCALE_UPDATE_INTERVAL_MS) {
            drawTimeScale();
        }
    }

    lastFrameTimestamp = currentTime;

    ctx.drawImage(canvas, shiftAmount, 0, canvasWidth - shiftAmount, canvasHeight, 0, 0, canvasWidth - shiftAmount, canvasHeight);
    pendingShiftAmount += shiftAmount;

    // Adaptive frame skipping for large FFTs - only affects audio processing
    frameSkipCounter++;
    if (frameSkipCounter < frameSkipThreshold) {
        // Skip audio processing but duplicate the last frame data for visual continuity
        if (window.lastProcessedFrameData) {
            paintRightEdgeInterpolated(previousFrameData || window.lastProcessedFrameData, window.lastProcessedFrameData, pendingShiftAmount);
            previousFrameData = window.lastProcessedFrameData;
            pendingShiftAmount = 0;
        }
        return;
    }
    frameSkipCounter = 0;

    if (!timeDomainFrame || !timeDomainFrame.buffer) {
        if (window.lastProcessedFrameData) {
            paintRightEdgeInterpolated(previousFrameData || window.lastProcessedFrameData, window.lastProcessedFrameData, pendingShiftAmount);
            previousFrameData = window.lastProcessedFrameData;
            pendingShiftAmount = 0;
        }
        return;
    }

    const timeDomainData = timeDomainFrame.buffer;
    latestTimeDomainData = null; // Consume this frame so we only process it once

    // Add to audio buffer for reprocessing.
    // The continuous buffer is appended on the audio callback (see
    // audioWorkletNode.port.onmessage), not here, so a throttled draw
    // rate doesn't lose audio frames.
    addToAudioBuffer(timeDomainData, currentWindowFunction, timeDomainFrame.timestamp);

    // Process FFT using the continuous buffer with overlap to minimize frame artifacts
    // Only process if we have enough data in the continuous buffer
    // Use hop size that balances time resolution with proper windowing overlap
    const hopSize = Math.max(128, currentFftSize / 64); // Better overlap for windowing, reduces sidebands

    // Check if buffer has enough data: either buffer has been filled once (after wraparound),
    // or current index is past the FFT size (initial fill)
    if (continuousAudioBuffer && (continuousBufferFilled || continuousBufferIndex >= currentFftSize)) {
        // Calculate hop size with proper circular buffer wraparound handling
        let actualHopSize = hopSize;

        if (window.lastFftBufferIndex !== undefined && window.lastFftBufferIndex !== null) {
            // Handle circular buffer wraparound
            if (continuousBufferIndex >= window.lastFftBufferIndex) {
                // Normal case - no wraparound
                actualHopSize = continuousBufferIndex - window.lastFftBufferIndex;
            } else {
                // Buffer has wrapped around
                actualHopSize = (continuousBufferSize - window.lastFftBufferIndex) + continuousBufferIndex;
            }
        }

        // Debug logging every 100 frames to avoid spam
        if (Math.random() < 0.01) {
            console.log('Buffer state:', {
                continuousBufferIndex,
                lastFftBufferIndex: window.lastFftBufferIndex,
                actualHopSize,
                hopSize,
                bufferSize: continuousBufferSize,
                wrapped: continuousBufferIndex < window.lastFftBufferIndex
            });
        }

        // Only process if we've actually advanced enough
        if (actualHopSize >= hopSize) {
            // Update the last FFT processing position
            window.lastFftBufferIndex = continuousBufferIndex;
        } else {
            // If we're not advancing enough, don't process yet
            if (actualHopSize === 0) {
                window.bufferStuckCounter = (window.bufferStuckCounter || 0) + 1;
                if (window.bufferStuckCounter > 100) {
                    console.warn('Buffer appears stuck, resetting lastFftBufferIndex');
                    window.lastFftBufferIndex = null;
                    window.bufferStuckCounter = 0;
                }
            } else {
                window.bufferStuckCounter = 0; // Reset counter if we're advancing, just not enough
            }
            return; // Don't process if we haven't advanced enough
        }

        // Extract FFT samples directly from the continuous buffer (no frame boundaries)
        const fftSamples = new Float32Array(currentFftSize);
        const startBufferPos = continuousBufferIndex - currentFftSize;

        // Debug info for first few samples
        const debugSamples = [];

        for (let i = 0; i < currentFftSize; i++) {
            // Get samples going backwards from current position
            let bufferPos = continuousBufferIndex - currentFftSize + i;

            // Handle circular buffer wraparound
            // Note: We only reach this code when continuousBufferIndex >= currentFftSize (line 554),
            // so we always have enough data and never need to pad with zeros
            if (bufferPos < 0) {
                bufferPos = (bufferPos + continuousBufferSize) % continuousBufferSize;
            } else {
                bufferPos = bufferPos % continuousBufferSize;
            }

            fftSamples[i] = continuousAudioBuffer[bufferPos];

            // Store debug info for first few and last few samples
            if (i < 5 || i >= currentFftSize - 5) {
                debugSamples.push({
                    i: i,
                    bufferPos: bufferPos,
                    value: fftSamples[i].toFixed(4)
                });
            }
        }

        // Log debug info occasionally
        if (Math.random() < 0.005) { // Very occasionally
            console.log('FFT extraction debug:', {
                fftSize: currentFftSize,
                continuousBufferIndex: continuousBufferIndex,
                startBufferPos: startBufferPos,
                samples: debugSamples
            });
        }

        // Convert to array for FFT library - let web worker handle windowing
        const fftInput = new Array(currentFftSize);
        for (let i = 0; i < currentFftSize; i++) {
            fftInput[i] = fftSamples[i] || 0;
        }

        // Check for discontinuities in the raw FFT samples
        let maxJump = 0;
        let jumpLocation = -1;
        let isAllZeros = true;
        let sampleVariance = 0;

        for (let i = 1; i < fftSamples.length; i++) {
            const jump = Math.abs(fftSamples[i] - fftSamples[i-1]);
            if (jump > maxJump) {
                maxJump = jump;
                jumpLocation = i;
            }
            if (Math.abs(fftSamples[i]) > 1e-6) {
                isAllZeros = false;
            }
            sampleVariance += fftSamples[i] * fftSamples[i];
        }
        sampleVariance /= fftSamples.length;

        // Detect potentially problematic data
        if (isAllZeros && Math.random() < 0.01) {
            console.warn('All-zero FFT input detected');
        }
        if (sampleVariance < 1e-10 && Math.random() < 0.01) {
            console.warn('Very low variance FFT input detected:', sampleVariance);
        }

        // Store the actual FFT input data for waveform visualization
        lastFftInputData = {
            data: [...fftInput], // Copy the FFT input data
            timestamp: Date.now(),
            fftSize: currentFftSize,
            bufferIndex: continuousBufferIndex,
            rawSamples: [...fftSamples], // Store raw samples before windowing for debugging
            maxJump: maxJump,
            jumpLocation: jumpLocation
        };

        // Always use Web Worker for FFT computation to keep main thread free
        if (fftWorker && !pendingFftRequest) {
            const requestId = ++fftRequestId;
            pendingFftRequest = { id: requestId, fftSize: currentFftSize };

            fftWorker.postMessage({
                id: requestId,
                data: fftInput,
                windowFunction: currentWindowFunction
            });
        }

        // Update waveform display
        drawWaveform();
    }
}

function drawLabels() {
    labelsCtx.clearRect(0, 0, labelsCanvas.width, labelsCanvas.height);

    labelsCtx.fillStyle = '#fff';
    labelsCtx.font = '10px Arial';
    labelsCtx.textAlign = 'right';
    labelsCtx.textBaseline = 'middle';

    const labelFrequencies = [50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];

    for (const freq of labelFrequencies) {
        if (freq < minFrequency || freq > maxFrequency) {
            continue; // Skip frequencies outside the current range
        }

        let yPos;
        if (isLogScale) {
            const logMin = frequencyToLog(minFrequency);
            const logMax = frequencyToLog(maxFrequency);
            const logFreq = frequencyToLog(freq);
            yPos = labelsCanvas.height - 1 - ((logFreq - logMin) / (logMax - logMin)) * (labelsCanvas.height - 1);
        } else {
            const linearMin = minFrequency;
            const linearMax = maxFrequency;
            yPos = labelsCanvas.height - 1 - ((freq - linearMin) / (linearMax - linearMin)) * (labelsCanvas.height - 1);
        }

        // Draw text label
        labelsCtx.fillText(freq >= 1000 ? (freq / 1000) + 'k' : freq, labelsCanvas.width - 5, yPos);

        // Draw a small line
        labelsCtx.strokeStyle = 'white';
        labelsCtx.lineWidth = 0.5;
        labelsCtx.beginPath();
        labelsCtx.moveTo(labelsCanvas.width - 10, yPos);
        labelsCtx.lineTo(labelsCanvas.width, yPos);
        labelsCtx.stroke();
    }
}

function getTimePerPixel() {
    return Math.max(lastDrawnTimePerPixel, MIN_TIME_PER_PIXEL);
}

function drawTimeScale() {
    if (!timeScaleCanvas || !timeScaleCtx) return;

    const canvasWidth = timeScaleCanvas.clientWidth || timeScaleCanvas.width;
    if (canvasWidth <= 0) {
        return;
    }
    const currentTimePerPixel = Math.max(smoothedTimePerPixel, MIN_TIME_PER_PIXEL);
    lastDrawnTimePerPixel = currentTimePerPixel;
    lastTimeScaleUpdate = typeof performance !== 'undefined' ? performance.now() : Date.now();
    lastTotalTimeSpan = canvasWidth * currentTimePerPixel;

    // Clear the canvas
    timeScaleCtx.clearRect(0, 0, timeScaleCanvas.width, timeScaleCanvas.height);

    // Set up the drawing context
    timeScaleCtx.fillStyle = '#fff';
    timeScaleCtx.font = '10px Arial';
    timeScaleCtx.textAlign = 'center';
    timeScaleCtx.textBaseline = 'top';
    
    
    // The time per frame depends on how often we process FFTs and the scrolling speed
    const timePerPixel = currentTimePerPixel;
    
    // Calculate total time span based on canvas width
    const totalTimeSpan = canvasWidth * timePerPixel;
    
    // Determine appropriate time intervals for labels
    let timeInterval;
    if (totalTimeSpan <= 1) {
        timeInterval = 0.1; // 100ms intervals
    } else if (totalTimeSpan <= 5) {
        timeInterval = 0.5; // 500ms intervals
    } else if (totalTimeSpan <= 10) {
        timeInterval = 1; // 1 second intervals
    } else if (totalTimeSpan <= 30) {
        timeInterval = 5; // 5 second intervals
    } else {
        timeInterval = 10; // 10 second intervals
    }
    
    // Draw time labels (0 at right edge, increasing towards left)
    const numLabels = Math.floor(totalTimeSpan / timeInterval) + 1;
    for (let i = 0; i < numLabels; i++) {
        const time = i * timeInterval;
        // Calculate x position from right edge (0) towards left (increasing time)
        const xPos = canvasWidth - (time / totalTimeSpan) * canvasWidth;
        
        // Only draw if the label is within the canvas
        if (xPos >= 0) {
            // Format time label
            let timeLabel;
            if (time < 1) {
                timeLabel = `${(time * 1000).toFixed(0)}ms`;
            } else if (time < 60) {
                timeLabel = `${time.toFixed(1)}s`;
            } else {
                const minutes = Math.floor(time / 60);
                const seconds = time % 60;
                timeLabel = `${minutes}:${seconds.toFixed(0).padStart(2, '0')}`;
            }
            
            // Draw tick mark at the top
            timeScaleCtx.strokeStyle = '#fff';
            timeScaleCtx.lineWidth = 1;
            timeScaleCtx.beginPath();
            timeScaleCtx.moveTo(xPos, 0);
            timeScaleCtx.lineTo(xPos, 10);
            timeScaleCtx.stroke();
            
            // Draw the label below the tick mark
            timeScaleCtx.fillText(timeLabel, xPos, 15);
        }
    }
    
    // Draw a horizontal line at the top
    timeScaleCtx.strokeStyle = '#ccc';
    timeScaleCtx.lineWidth = 1;
    timeScaleCtx.beginPath();
    timeScaleCtx.moveTo(0, 0);
    timeScaleCtx.lineTo(canvasWidth, 0);
    timeScaleCtx.stroke();
}

window.onload = () => {
    // Get references to all control elements
    canvas = document.getElementById('spectrogram');
    ctx = canvas.getContext('2d');


    labelsCanvas = document.getElementById('frequency-labels');
    labelsCtx = labelsCanvas.getContext('2d');

    cursorLineCanvas = document.getElementById('cursor-line-canvas');
    cursorLineCtx = cursorLineCanvas.getContext('2d');

    timeScaleCanvas = document.getElementById('time-scale');
    timeScaleCtx = timeScaleCanvas ? timeScaleCanvas.getContext('2d') : null;

    waveformCanvas = document.getElementById('waveform-canvas');
    waveformCtx = waveformCanvas.getContext('2d');
    waveformArea = document.querySelector('.waveform-area');
    spectrogramArea = document.querySelector('.main-spectrogram-area');
    resizeHandle = document.getElementById('resize-handle');

    populateBuildInfo();

    // Initialize waveform canvas size
    if (waveformCanvas) {
        waveformCanvas.width = canvas.clientWidth || canvas.width || 1000;
        waveformCanvas.height = waveformHeight; // Use default waveform height
    }

    pauseResumeBtn = document.getElementById('pause-resume-btn');
    const minFreqSlider = document.getElementById('min-freq');
    const maxFreqSlider = document.getElementById('max-freq');
    const minFreqDisplay = document.getElementById('min-freq-display');
    const maxFreqDisplay = document.getElementById('max-freq-display');
    const colorSchemeSelect = document.getElementById('color-scheme');
    logScaleCheckbox = document.getElementById('log-scale-checkbox');
    fftSizeSelect = document.getElementById('fft-size');
    sampleRateSelect = document.getElementById('sample-rate');
    const scrollingSpeedSlider = document.getElementById('scrolling-speed');
    const scrollingSpeedDisplay = document.getElementById('scrolling-speed-display');
    const phosphorDecaySlider = document.getElementById('phosphor-decay');
    const phosphorDecayDisplay = document.getElementById('phosphor-decay-display');
    selectionOverlay = document.getElementById('selection-overlay');
    const mainSpectrogramArea = document.querySelector('.main-spectrogram-area');
    fpsDisplay = document.getElementById('fps-display');

    // Tapping/clicking anywhere on the waveform toggles pause.
    if (waveformCanvas) {
        waveformCanvas.addEventListener('click', () => {
            pauseResumeBtn.click();
        });

        const clampZoom = (z) =>
            Math.max(MIN_WAVEFORM_TIME_ZOOM, Math.min(MAX_WAVEFORM_TIME_ZOOM, z));

        // Mouse wheel + touchpad pinch. Browsers translate a trackpad pinch
        // into a wheel event with ctrlKey=true; we treat both the same — any
        // wheel over the waveform zooms the time axis. deltaY>0 (scroll down
        // or pinch in) zooms out (more time); deltaY<0 zooms in (less time).
        waveformCanvas.addEventListener('wheel', (e) => {
            e.preventDefault();
            const factor = Math.pow(1.0015, e.deltaY);
            waveformTimeZoom = clampZoom(waveformTimeZoom * factor);
        }, { passive: false });

        // Two-finger pinch on touchscreens.
        let pinchPrevDistance = 0;
        waveformCanvas.addEventListener('touchstart', (e) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                pinchPrevDistance = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY,
                );
            }
        }, { passive: false });

        waveformCanvas.addEventListener('touchmove', (e) => {
            if (e.touches.length !== 2) return;
            e.preventDefault();
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY,
            );
            if (pinchPrevDistance > 0) {
                // Fingers apart (distance grows) → zoom in → less time shown.
                const ratio = distance / pinchPrevDistance;
                waveformTimeZoom = clampZoom(waveformTimeZoom / ratio);
            }
            pinchPrevDistance = distance;
        }, { passive: false });

        const resetPinch = () => { pinchPrevDistance = 0; };
        waveformCanvas.addEventListener('touchend', resetPinch);
        waveformCanvas.addEventListener('touchcancel', resetPinch);
    }

    // Set up pause/resume button
    pauseResumeBtn.addEventListener('click', () => {
        isPaused = !isPaused;
        if (isPaused) {
            pauseResumeBtn.textContent = 'Resume';
            lastFrameTimestamp = null;
        } else {
            pauseResumeBtn.textContent = 'Pause';
            // If resuming, we need to kick off the draw loop again.
            if (!audioContext) {
                // Audio was released (tab was hidden or never started); re-acquire.
                const rate = sampleRateSelect ? parseInt(sampleRateSelect.value) : 44100;
                lastFrameTimestamp = null;
                startAudio(rate);
            } else if (audioContext.state === 'running') {
                lastFrameTimestamp = null;
                draw();
            }
        }
    });

    // Set up toggle controls button
    toggleControlsBtn = document.getElementById('toggle-controls-btn');
    toggleControlsBtn.addEventListener('click', () => {
        const controls = document.querySelector('.controls');
        const fpsCounter = document.querySelector('.fps-counter');
        if (controls.style.display === 'none') {
            controls.style.display = 'flex';
            fpsCounter.style.display = 'block';
            toggleControlsBtn.textContent = 'Hide Settings';
        } else {
            controls.style.display = 'none';
            fpsCounter.style.display = 'none';
            toggleControlsBtn.textContent = 'Show Settings';
        }
        // Trigger resize to recalculate canvas positions
        setTimeout(() => resizeCanvas(), 0);
    });

    // Initialize WebFFT instance
    try {
        fftInstance = new webfft(currentFftSize);
    } catch (error) {
        console.error('Failed to initialize WebFFT with size', currentFftSize, ':', error);
        alert('Error: Failed to initialize WebFFT. Please refresh the page.');
    }

    startAudio(parseInt(sampleRateSelect.value));
    resizeCanvas();
    updateFrequencyMappings(); // Initialize frequency mappings
    initFftWorker(); // Initialize FFT Web Worker
    drawTimeScale(); // Initialize time scale

    // Initialize slider values and displays
    minFreqSlider.value = logToLinear(frequencyToLog(minFrequency)) * 1000;
    maxFreqSlider.value = logToLinear(frequencyToLog(maxFrequency)) * 1000;
    minFreqDisplay.textContent = minFrequency;
    maxFreqDisplay.textContent = maxFrequency;

    // Initialize color scheme dropdown
    colorSchemeSelect.value = currentColorScheme;
    // Add event listeners for frequency sliders
    minFreqSlider.addEventListener('input', (event) => {
        const linearValue = parseInt(event.target.value) / 1000;
        minFrequency = Math.round(logToFrequency(linearToLog(linearValue)));
        minFreqDisplay.textContent = minFrequency;
        // Ensure minFreq is not greater than maxFreq
        if (minFrequency >= maxFrequency) {
            maxFrequency = minFrequency + 1; // Ensure max is always greater than min
            maxFreqSlider.value = logToLinear(frequencyToLog(maxFrequency)) * 1000;
            maxFreqDisplay.textContent = maxFrequency;
        }
        drawLabels(); // Redraw labels on frequency change
        updateFrequencyMappings(); // Update frequency mappings
        if (isPaused) {
            reprocessAudioBuffer(); // Reprocess with new frequency range
        }
    });

    function getFrequencyFromY(y) {
        const canvasHeight = canvas.height;
        let freq;
        if (isLogScale) {
            const logMin = frequencyToLog(minFrequency);
            const logMax = frequencyToLog(maxFrequency);
            const logFreq = logMin + ((canvasHeight - 1 - y) / (canvasHeight - 1)) * (logMax - logMin);
            freq = logToFrequency(logFreq);
        } else {
            const linearMin = minFrequency;
            const linearMax = maxFrequency;
            freq = linearMin + ((canvasHeight - 1 - y) / (canvasHeight - 1)) * (linearMax - linearMin);
        }
        return freq;
    }

    maxFreqSlider.addEventListener('input', (event) => {
        const linearValue = parseInt(event.target.value) / 1000;
        maxFrequency = Math.round(logToFrequency(linearToLog(linearValue)));
        maxFreqDisplay.textContent = maxFrequency;
        // Ensure maxFreq is not less than minFreq
        if (maxFrequency <= minFrequency) {
            minFrequency = maxFrequency - 1; // Ensure min is always less than max
            if (minFrequency < 20) minFrequency = 20; // Don't go below 20Hz
            minFreqSlider.value = logToLinear(frequencyToLog(minFrequency)) * 1000;
            minFreqDisplay.textContent = minFrequency;
        }
        drawLabels(); // Redraw labels on frequency change
        updateFrequencyMappings(); // Update frequency mappings
        if (isPaused) {
            reprocessAudioBuffer(); // Reprocess with new frequency range
        }
    });

    colorSchemeSelect.addEventListener('change', (event) => {
        currentColorScheme = event.target.value;
        colorCache.clear(); // Clear color cache when scheme changes
        if (isPaused) {
            reprocessAudioBuffer(); // Reprocess with new color scheme
        }
    });

    function handleFrequencyScaleChange(event) {
        isLogScale = event.target.checked;
        drawLabels(); // Redraw labels on scale change
        updateFrequencyMappings(); // Update frequency mappings
        if (isPaused) {
            reprocessAudioBuffer(); // Reprocess with new frequency scale
        }
    }

    // Make handleFrequencyScaleChange globally accessible
    window.handleFrequencyScaleChange = handleFrequencyScaleChange;

    logScaleCheckbox.addEventListener('change', handleFrequencyScaleChange);

    // Set default selected value (convert FFT size to log2)
    fftSizeSelect.value = Math.log2(currentFftSize);
    fftSizeSelect.addEventListener('change', async (event) => {
        // Convert slider value (log2) to actual FFT size
        const newFftSize = Math.pow(2, parseInt(event.target.value));
        console.log('FFT size changed:', { old: currentFftSize, new: newFftSize });
        currentFftSize = newFftSize;

        // Update frame skip threshold based on FFT size - more aggressive processing for time resolution
        if (currentFftSize >= 32768) {
            frameSkipThreshold = 2; // Skip every other frame only for extremely large FFTs
        } else {
            frameSkipThreshold = 1; // Process every frame for all other sizes
        }

        // Create new WebFFT instance for the new size
        try {
            fftInstance = new webfft(currentFftSize);
        } catch (error) {
            console.error('Failed to initialize WebFFT with size', currentFftSize, ':', error);
            alert('Error: FFT size ' + currentFftSize + ' is not supported by WebFFT. Please choose a different size.');
            return;
        }

        // Reset reprocessing flag before restarting audio
        isReprocessing = false;
        
        // Re-initialize audio context with new FFT size
        if (audioContext) {
            await audioContext.close();
            audioContext = null;
        }
        // Cancel any existing animation frame before starting a new one
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
        }
        startAudio(parseInt(sampleRateSelect.value));
        // Add a small delay to ensure currentFftSize is properly set
        setTimeout(() => {
            console.log('Delayed reprocessing with FFT size:', currentFftSize);
            drawTimeScale(); // Update time scale with new FFT size
            if (isPaused) {
                reprocessAudioBuffer(); // Reprocess with new FFT size
            }
        }, 100);
    });

    const windowFunctionSelect = document.getElementById('window-function');
    // Map slider values to window function names
    const windowFunctionMap = ['blackman', 'hann', 'hamming', 'rectangular'];
    // Set default selected value (convert window function name to slider position)
    windowFunctionSelect.value = windowFunctionMap.indexOf(currentWindowFunction);
    windowFunctionSelect.addEventListener('change', (event) => {
        // Convert slider value to window function name
        currentWindowFunction = windowFunctionMap[parseInt(event.target.value)];
        if (isPaused) {
            reprocessAudioBuffer(); // Reprocess with new window function
        }
    });

    sampleRateSelect.addEventListener('change', async (event) => {
        await restartAudioContext(parseInt(event.target.value));
    });

    // Add event listeners for mouse selection on the spectrogram
    canvas.addEventListener('mousedown', (event) => {
        isSelecting = true;
        selectionStart = { x: event.offsetX, y: event.offsetY };
        selectionOverlay.style.display = 'block';
        selectionOverlay.style.top = `${selectionStart.y}px`;
        selectionOverlay.style.height = '0px';
    });

    canvas.addEventListener('mousemove', (event) => {
        if (isSelecting) {
            selectionEnd = { x: event.offsetX, y: event.offsetY };
            const startY = Math.min(selectionStart.y, selectionEnd.y);
            const height = Math.abs(selectionStart.y - selectionEnd.y);
            selectionOverlay.style.top = `${startY}px`;
            selectionOverlay.style.height = `${height}px`;
        }
    });

    canvas.addEventListener('mouseup', (event) => {
        if (isSelecting) {
            isSelecting = false;
            selectionOverlay.style.display = 'none';

            const y1 = selectionStart.y;
            const y2 = selectionEnd.y;

            // Determine the new frequency range based on the selection
            const newMinFreq = getFrequencyFromY(Math.max(y1, y2));
            const newMaxFreq = getFrequencyFromY(Math.min(y1, y2));

            if (Math.abs(y1 - y2) > 5) { // Only zoom if selection is significant
                minFrequency = Math.round(newMinFreq);
                maxFrequency = Math.round(newMaxFreq);

                // Update sliders and displays
                minFreqSlider.value = logToLinear(frequencyToLog(minFrequency)) * 1000;
                maxFreqSlider.value = logToLinear(frequencyToLog(maxFrequency)) * 1000;
                minFreqDisplay.textContent = minFrequency;
                maxFreqDisplay.textContent = maxFrequency;

                drawLabels();
                updateFrequencyMappings(); // Update frequency mappings after zoom
                if (isPaused) {
                    reprocessAudioBuffer(); // Reprocess with new frequency range
                }
            }
        }
    });

    canvas.addEventListener('dblclick', () => {
        // Reset to default frequency range
        minFrequency = 20;
        maxFrequency = 11025;

        // Update sliders and displays
        minFreqSlider.value = logToLinear(frequencyToLog(minFrequency)) * 1000;
        maxFreqSlider.value = logToLinear(frequencyToLog(maxFrequency)) * 1000;
        minFreqDisplay.textContent = minFrequency;
        maxFreqDisplay.textContent = maxFrequency;

        drawLabels();
        updateFrequencyMappings(); // Update frequency mappings after reset
        if (isPaused) {
            reprocessAudioBuffer(); // Reprocess with new frequency range
        }
    });

    const frequencyTooltip = document.getElementById('frequency-tooltip');

    const minDbSlider = document.getElementById('min-db');
    const maxDbSlider = document.getElementById('max-db');
    const minDbDisplay = document.getElementById('min-db-display');
    const maxDbDisplay = document.getElementById('max-db-display');

    minDbSlider.addEventListener('input', (event) => {
        minDbDisplay.textContent = event.target.value;
        if (isPaused) {
            reprocessAudioBuffer(); // Reprocess with new decibel range
        }
    });

    maxDbSlider.addEventListener('input', (event) => {
        maxDbDisplay.textContent = event.target.value;
        if (isPaused) {
            reprocessAudioBuffer(); // Reprocess with new decibel range
        }
    });

    // Scrolling speed control
    scrollingSpeedSlider.addEventListener('input', (event) => {
        scrollingSpeed = parseFloat(event.target.value);
        scrollingSpeedDisplay.textContent = scrollingSpeed.toFixed(1);
        drawTimeScale(); // Update time scale to reflect new scrolling speed
    });

    // Phosphor decay control
    phosphorDecaySlider.addEventListener('input', (event) => {
        phosphorDecayMs = parseInt(event.target.value);
        phosphorDecayDisplay.textContent = phosphorDecayMs;
    });

    const anchorWeightSlider = document.getElementById('anchor-weight');
    const anchorWeightDisplay = document.getElementById('anchor-weight-display');
    if (anchorWeightSlider && anchorWeightDisplay) {
        anchorWeightSlider.addEventListener('input', (event) => {
            waveformAnchorWeight = parseFloat(event.target.value);
            anchorWeightDisplay.textContent = waveformAnchorWeight.toFixed(1);
        });
    }

    const fpsLimitSlider = document.getElementById('fps-limit');
    const fpsLimitDisplay = document.getElementById('fps-limit-display');
    if (fpsLimitSlider && fpsLimitDisplay) {
        fpsLimitSlider.addEventListener('input', (event) => {
            targetDrawFps = parseInt(event.target.value, 10);
            targetDrawIntervalMs = 1000 / targetDrawFps;
            fpsLimitDisplay.textContent = targetDrawFps;
        });
    }

    const TOUCH_TOOLTIP_Y_OFFSET = 60; // keep the tooltip clear of the fingertip

    const updateCrosshair = (x, y, isTouchInput = false) => {
        mouseCanvasX = x;
        mouseCanvasY = y;
        drawCursorLine(mouseCanvasX, mouseCanvasY);
        if (isSelecting) return;

        const freq = getFrequencyFromY(y);
        const timeOffsetSeconds = getTimeOffsetFromX(x);
        const note = frequencyToNote(freq);
        const notePart = note
            ? ` · ${note.name} ${note.cents >= 0 ? '+' : ''}${note.cents}¢`
            : '';
        frequencyTooltip.textContent = `${freq.toFixed(2)} Hz${notePart} | ${timeOffsetSeconds.toFixed(2)}s ago`;

        let tooltipX = x + canvas.offsetLeft - frequencyTooltip.offsetWidth - 10;
        let tooltipY = isTouchInput
            ? y + canvas.offsetTop - frequencyTooltip.offsetHeight - TOUCH_TOOLTIP_Y_OFFSET
            : y + canvas.offsetTop - (frequencyTooltip.offsetHeight / 2);
        const mainSpectrogramAreaRect = mainSpectrogramArea.getBoundingClientRect();
        if (tooltipX < 0) tooltipX = x + canvas.offsetLeft + 10;
        if (tooltipY < 0) {
            // Finger near the top of the canvas — flip the tooltip below instead.
            tooltipY = isTouchInput
                ? y + canvas.offsetTop + TOUCH_TOOLTIP_Y_OFFSET
                : 0;
        }
        if (tooltipY + frequencyTooltip.offsetHeight > mainSpectrogramAreaRect.height) {
            tooltipY = mainSpectrogramAreaRect.height - frequencyTooltip.offsetHeight - 5;
        }

        frequencyTooltip.style.left = `${tooltipX}px`;
        frequencyTooltip.style.top = `${tooltipY}px`;
        frequencyTooltip.style.display = 'block';
    };

    const hideCrosshair = () => {
        frequencyTooltip.style.display = 'none';
        mouseCanvasX = -1;
        mouseCanvasY = -1;
        cursorLineCtx.clearRect(0, 0, cursorLineCanvas.width, cursorLineCanvas.height);
    };

    const canvasCoordsFromTouch = (touch) => {
        const rect = canvas.getBoundingClientRect();
        return {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top,
        };
    };

    canvas.addEventListener('mousemove', (event) => {
        updateCrosshair(event.offsetX, event.offsetY);
    });

    canvas.addEventListener('mouseenter', () => {
        if (!isSelecting) {
            frequencyTooltip.style.display = 'block';
        }
    });

    canvas.addEventListener('mouseleave', hideCrosshair);

    // Touch support for the crosshair/tooltip. Non-passive so preventDefault()
    // stops the page from scrolling while the finger is dragging across the
    // spectrogram.
    canvas.addEventListener('touchstart', (event) => {
        if (event.touches.length === 0) return;
        event.preventDefault();
        const { x, y } = canvasCoordsFromTouch(event.touches[0]);
        updateCrosshair(x, y, true);
    }, { passive: false });

    canvas.addEventListener('touchmove', (event) => {
        if (event.touches.length === 0) return;
        event.preventDefault();
        const { x, y } = canvasCoordsFromTouch(event.touches[0]);
        updateCrosshair(x, y, true);
    }, { passive: false });

    // Intentionally no touchend/touchcancel hide — on touchscreens there's
    // no hover state, so leaving the crosshair and tooltip visible after the
    // finger lifts lets the user read the value without keeping contact. The
    // next touchstart moves them to the new position.

    // Set up resize handle drag functionality (mouse + touch)
    const applyDragMove = (clientY) => {
        const containerRect = document.querySelector('.container').getBoundingClientRect();
        const containerBottom = containerRect.bottom;
        // 20px for margins and handle
        const newHeight = Math.max(50, containerBottom - clientY - 20);

        waveformHeight = newHeight;
        waveformArea.style.flexBasis = `${newHeight}px`;
        waveformArea.style.flexGrow = '0';
        waveformArea.style.flexShrink = '0';

        setTimeout(() => resizeCanvas(), 0);
    };

    if (resizeHandle) {
        resizeHandle.addEventListener('mousedown', (e) => {
            isDragging = true;
            e.preventDefault();
        });
        // touchstart must be non-passive so preventDefault() can stop the
        // browser from treating the gesture as a page scroll.
        resizeHandle.addEventListener('touchstart', (e) => {
            if (e.touches.length === 0) return;
            isDragging = true;
            e.preventDefault();
        }, { passive: false });
    }

    window.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        applyDragMove(e.clientY);
    });

    window.addEventListener('touchmove', (e) => {
        if (!isDragging || e.touches.length === 0) return;
        e.preventDefault();
        applyDragMove(e.touches[0].clientY);
    }, { passive: false });

    const endDrag = () => { if (isDragging) isDragging = false; };
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('touchend', endDrag);
    window.addEventListener('touchcancel', endDrag);
};

function drawCursorLine(x, y) {
    cursorLineCtx.clearRect(0, 0, cursorLineCanvas.width, cursorLineCanvas.height); // Clear previous line
    cursorLineCtx.strokeStyle = 'rgba(255, 255, 255, 0.5)'; // Thin white, slightly transparent line
    cursorLineCtx.lineWidth = 1;

    if (y !== -1) {
        cursorLineCtx.beginPath();
        cursorLineCtx.moveTo(0, y);
        cursorLineCtx.lineTo(cursorLineCanvas.width, y);
        cursorLineCtx.stroke();
    }

    if (x !== -1) {
        cursorLineCtx.beginPath();
        cursorLineCtx.moveTo(x, 0);
        cursorLineCtx.lineTo(x, cursorLineCanvas.height);
        cursorLineCtx.stroke();
    }
}

function getFrequencyFromY(y) {
    const canvasHeight = canvas.height;
    let freq;
    if (isLogScale) {
        const logMin = frequencyToLog(minFrequency);
        const logMax = frequencyToLog(maxFrequency);
        const logFreq = logMin + ((canvasHeight - 1 - y) / (canvasHeight - 1)) * (logMax - logMin);
        freq = logToFrequency(logFreq);
    } else {
        const linearMin = minFrequency;
        const linearMax = maxFrequency;
        freq = linearMin + ((canvasHeight - 1 - y) / (canvasHeight - 1)) * (linearMax - linearMin);
    }
    return freq;
}

function getTimeOffsetFromX(x) {
    if (!canvas) {
        return 0;
    }
    const canvasWidth = canvas.clientWidth || canvas.width;
    if (canvasWidth <= 0) {
        return 0;
    }
    const clampedX = Math.min(Math.max(x, 0), canvasWidth);
    const totalTimeSpan = lastTotalTimeSpan || (canvasWidth * getTimePerPixel());
    const fractionFromRight = (canvasWidth - clampedX) / canvasWidth;
    return fractionFromRight * totalTimeSpan;
}

window.addEventListener('resize', resizeCanvas);

// Page Visibility: when the tab is backgrounded, release the mic entirely —
// not just suspend the AudioContext. Suspending keeps the MediaStreamTrack
// active, which leaves the OS recording indicator lit. Stopping the track
// turns the indicator off; when the tab returns we re-acquire via
// getUserMedia (no re-prompt after the initial permission grant).
document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
        isVisibilityHidden = true;
        releaseAudioResources();
    } else {
        isVisibilityHidden = false;
        if (!isPaused && !audioContext) {
            const rate = sampleRateSelect
                ? parseInt(sampleRateSelect.value)
                : 44100;
            lastFrameTimestamp = null;
            startAudio(rate);
        }
    }
});

// Draggable resize handle between spectrogram and waveform
let resizeHandle;
let waveformArea;
let spectrogramArea;
let isDragging = false;
let waveformHeight = 100; // Default height in pixels

window.addEventListener('keydown', (event) => {
    if (event.code === 'Space') {
        pauseResumeBtn.click();
    } else if (event.key === '`' || event.key === '~') {
        toggleControlsBtn.click();
    } else if (event.key === 'l') {
        isLogScale = !isLogScale;
        logScaleCheckbox.checked = isLogScale;
        logScaleCheckbox.dispatchEvent(new Event('change'));
        handleFrequencyScaleChange({ target: logScaleCheckbox });
    } else if (event.key === 'F') {
        // Increase FFT size (slider value is log2, so increment by 1)
        const currentLog2 = parseInt(fftSizeSelect.value);
        if (currentLog2 < 15) { // Max is 15 (32768)
            fftSizeSelect.value = currentLog2 + 1;
            fftSizeSelect.dispatchEvent(new Event('change'));
        }
    } else if (event.key === 'f') {
        // Decrease FFT size (slider value is log2, so decrement by 1)
        const currentLog2 = parseInt(fftSizeSelect.value);
        if (currentLog2 > 10) { // Min is 10 (1024)
            fftSizeSelect.value = currentLog2 - 1;
            fftSizeSelect.dispatchEvent(new Event('change'));
        }
    } else if (event.key === 'S') {
        // Increase Sample Rate
        const sampleRates = [8000, 16000, 22050, 32000, 44100, 48000, 96000];
        const currentSampleRate = parseInt(sampleRateSelect.value);
        const currentIndex = sampleRates.indexOf(currentSampleRate);
        if (currentIndex < sampleRates.length - 1) {
            sampleRateSelect.value = sampleRates[currentIndex + 1];
            sampleRateSelect.dispatchEvent(new Event('change'));
        }
    } else if (event.key === 's') {
        // Decrease Sample Rate
        const sampleRates = [8000, 16000, 22050, 32000, 44100, 48000, 96000];
        const currentSampleRate = parseInt(sampleRateSelect.value);
        const currentIndex = sampleRates.indexOf(currentSampleRate);
        if (currentIndex > 0) {
            sampleRateSelect.value = sampleRates[currentIndex - 1];
            sampleRateSelect.dispatchEvent(new Event('change'));
        }
    } else if (event.key === 'Escape') {
        // Cancel reprocessing
        if (isReprocessing || window.reprocessingInProgress) {
            console.log('Cancelling reprocessing via Escape key');
            
            // Cancel any ongoing reprocessing
            isReprocessing = false;
            window.reprocessingInProgress = false;
            window.reprocessingRetryCount = 0;
            pendingFftRequest = null;
            
            // Clear any timeouts
            if (window.workerTimeout) {
                clearTimeout(window.workerTimeout);
                window.workerTimeout = null;
            }
            
            // Clear any main reprocessing timeout
            if (window.reprocessingTimeout) {
                clearTimeout(window.reprocessingTimeout);
                window.reprocessingTimeout = null;
            }
            
            // Reset column counter
            window.reprocessingColumn = undefined;
        }
    }
});
